import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { motion } from "framer-motion";
import { ShoppingBag, ShieldCheck, ArrowRight, Sparkles, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const CONDITION_LABEL = {
  excellent: "Excellent",
  "very-good": "Very Good",
  good: "Good",
  fair: "Fair",
};
const CONDITION_STYLE = {
  excellent: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  "very-good": "bg-primary/15 text-primary border-primary/30",
  good: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  fair: "bg-slate-500/15 text-slate-600 border-slate-500/30",
};

export default function ResaleShowcase({ homepage = false }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [businessWA, setBusinessWA] = useState("");

  useEffect(() => {
    api.get("/resale").then(({ data }) => setProducts(data)).finally(() => setLoading(false));
    api.get("/config").then(({ data }) => setBusinessWA(data.business_whatsapp || "")).catch(() => {});
  }, []);

  const list = homepage ? products.slice(0, 8) : products;

  const buyOnWhatsApp = (p) => {
    const msg = encodeURIComponent(
      `Hi Tech Value! 👋\n\nI want to buy this refurbished phone from your Tech World store:\n\n${p.name}${p.storage ? " · " + p.storage : ""}\nCondition: ${CONDITION_LABEL[p.condition] || p.condition}\nPrice: ${formatINR(p.price)}\n\nCan I get more details?`
    );
    return `https://wa.me/${businessWA || "918104878405"}?text=${msg}`;
  };

  if (loading) {
    return (
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-72 rounded-2xl bg-secondary animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (list.length === 0 && homepage) return null;

  return (
    <section id="shop" className="relative py-16 lg:py-20 overflow-hidden">
      <div className="absolute inset-0 tv-dot-bg opacity-[0.05] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <span className="tv-chip-emerald mb-3">
              <ShoppingBag className="w-3.5 h-3.5" /> {homepage ? "Available for Purchase" : "Tech Value Shop"}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter">
              Premium refurbished phones. <br className="hidden sm:block" />
              <span className="text-primary">Warranty included.</span>
            </h2>
            <p className="text-muted-foreground mt-2 text-base sm:text-lg max-w-2xl">
              Every device hand-inspected across a 32-point test, sanitised, and shipped with a{" "}
              <span className="font-semibold text-foreground">3-month Tech Value warranty</span>.
            </p>
          </div>
          {homepage && list.length > 0 && (
            <a href="/shop"><Button variant="outline" data-testid="view-all-resale">View All →</Button></a>
          )}
        </div>

        {list.length === 0 ? (
          <div className="rounded-3xl border-2 border-dashed border-border p-14 text-center text-muted-foreground">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No refurbished phones in stock right now. Check back soon!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {list.map((p, idx) => {
              const discount = p.original_price && p.original_price > p.price
                ? Math.round((1 - p.price / p.original_price) * 100) : 0;
              return (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  className="group tv-gradient-border overflow-hidden flex flex-col"
                  data-testid={`resale-card-${p.id}`}
                >
                  <div className="aspect-[4/3] bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 relative overflow-hidden">
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className={`absolute top-2 left-2 tv-chip text-[10px] ${CONDITION_STYLE[p.condition]}`}>
                      <Sparkles className="w-3 h-3" /> {CONDITION_LABEL[p.condition] || p.condition}
                    </div>
                    {discount > 0 && (
                      <div className="absolute top-2 right-2 bg-gradient-to-r from-red-600 to-orange-500 text-white text-xs font-bold rounded-lg px-2 py-1 shadow-lg">
                        −{discount}%
                      </div>
                    )}
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <div className="font-bold leading-tight line-clamp-2">{p.name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{p.storage || "—"} · {p.warranty_months}mo warranty</div>
                    <div className="mt-3 flex items-end gap-2">
                      <span className="font-mono text-2xl font-black text-accent">{formatINR(p.price)}</span>
                      {p.original_price > p.price && (
                        <span className="font-mono text-xs text-muted-foreground line-through mb-1">{formatINR(p.original_price)}</span>
                      )}
                    </div>
                    {p.description && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{p.description}</p>
                    )}
                    <a href={buyOnWhatsApp(p)} target="_blank" rel="noreferrer" className="mt-3" data-testid={`buy-resale-${p.id}`}>
                      <Button className="w-full bg-[#25D366] hover:bg-[#20BD5A] text-white gap-2">
                        <MessageCircle className="w-4 h-4" /> Buy on WhatsApp
                      </Button>
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-5 flex items-start gap-3 text-sm">
          <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-foreground">Every phone comes with Tech Value Assurance</div>
            <div className="text-muted-foreground mt-0.5">3-month warranty · 7-day easy return · Free sanitisation · Free data-wipe demo · Original charger included wherever possible.</div>
          </div>
        </div>
      </div>
    </section>
  );
}
